import { useMemo } from "react";
import getConfig from "next/config";
import type { GetServerSidePropsContext } from "next";
import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache } from "@apollo/client";
import { sha256 } from "crypto-hash";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import customFetch from "./services/utils/apolloCustomFetch";
import { iHeaders } from "./services/queries/interfaces/headers";

export type ApolloClientType = ApolloClient<NormalizedCacheObject>;

const isServer = typeof window === "undefined";

/**
 * Browser-side per-(locale, tld) cache.
 *
 * On the server a fresh ApolloClient MUST be created per request, otherwise
 * per-request state (InMemoryCache, parsed query AST cache inside
 * createPersistedQueryLink, in-flight Link contexts) accumulates and leaks
 * memory across requests.
 *
 * On the client we reuse one client per (locale, tld) so the cache and
 * persisted-query hash map are amortised across renders / route changes.
 * We keep locale in the key because GraphQL results are locale-scoped,
 * even though the endpoint path itself is not locale-prefixed.
 */
const browserApolloClients = new Map<string, ApolloClient<NormalizedCacheObject>>();

const createApolloUri = (locale: string, topLevelDomain: string): string => {
	const { publicRuntimeConfig } = getConfig();
	// Browser base URL must be same-origin (or CORS-allowed) for local/dev use.
	// Using localhost while browsing a different host can trigger preflight CORS errors.
	const clientSideBase = process.env.NEXT_PUBLIC_LOCAL_BASE || "";
	const serverSideBase = publicRuntimeConfig?.drupal[topLevelDomain]?.gql || publicRuntimeConfig?.drupal?.com.gql;
	const base = isServer ? serverSideBase : clientSideBase;

	// Drupal's language negotiation is url path_prefix based, so the locale
	// must be in the request path for content not resolved via an explicit
	// `language` GraphQL argument (e.g. menu titles).
	return `${base}/${publicRuntimeConfig.subdir}/${locale}/graphql`;
};

const createApolloClient = (
	locale?: string,
	topLevelDomain?: string,
	ctx?: GetServerSidePropsContext | null,
	cookies?: string | null,
	getReq?: boolean,
	requestHeaders?: iHeaders,
) => {
	const isGetGraphQl = process.env.NEXT_PUBLIC_GET_GRAPHQL || false;
	const ssrRequestHeaders = isServer ? requestHeaders || {} : {};

	const httpLink = new HttpLink({
		uri: createApolloUri(locale || "en", topLevelDomain || "com"),
		credentials: "include",
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		fetch: customFetch,
		fetchOptions: {
			//mode: "no-cors",
			cookie: cookies,
		},
		headers: { cookie: cookies || "", "Apollo-Require-Preflight": true, ...ssrRequestHeaders },
	});

	const afterWareLink = new ApolloLink((operation, forward) =>
		forward(operation).map((response: any) => {
			const context = operation.getContext();

			const headers: iHeaders = {
				status: context.response.status,
				statusText: context.response.statusText,
			};
			if (response.data) {
				context.response.headers.forEach((perHeaderValue: string, perHeaderName: string) => {
					headers[perHeaderName] = perHeaderValue;
				});

				response.data.pageMetaInformation = {
					PurgeCacheTags: context.response.headers.get("Purge-Cache-Tags") || "",
					headers,
				};

				Object.keys(response.data).forEach((queryName: string) => {
					if (response.data[queryName]?.feTempStorage === null) {
						response.data[queryName].feTempStorage = {
							__headers: headers,
						};
					}
				});
			}
			return response;
		}),
	);

	const persistedQueryLink = createPersistedQueryLink({
		sha256,
		useGETForHashedQueries: true,
	}).concat(httpLink);

	return new ApolloClient({
		ssrMode: isServer,
		link:
			getReq && isGetGraphQl === "true" ? from([afterWareLink, persistedQueryLink]) : from([afterWareLink, httpLink]),
		cache: new InMemoryCache(),
		assumeImmutableResults: true,
		defaultOptions: {
			watchQuery: {
				errorPolicy: "all",
				fetchPolicy: "no-cache",
			},
			query: {
				errorPolicy: "all",
				fetchPolicy: "no-cache",
			},
		},
	});
};

export function initializeApollo(
	locale = "en",
	topLevelDomain = "",
	ctx = null,
	cookies: string | null = null,
	getReq?: boolean,
	initialState?: NormalizedCacheObject | null,
	requestHeaders?: iHeaders,
): ApolloClient<NormalizedCacheObject> {
	// SSR: always a brand new client per request. It becomes GC-eligible once
	// the response is sent. Callers SHOULD also invoke `apollo.stop()` and
	// `apollo.clearStore()` after all queries finish to release memory sooner.
	if (isServer) {
		return createApolloClient(locale, topLevelDomain, ctx, cookies, getReq, requestHeaders);
	}

	// Browser: reuse one client per (locale, tld). The locale is baked into
	// the GraphQL endpoint URI so we cannot share clients across locales.
	const key = `${locale}|${topLevelDomain}|${getReq ? "1" : "0"}`;
	let client = browserApolloClients.get(key);
	if (!client) {
		client = createApolloClient(locale, topLevelDomain, ctx, cookies, getReq, requestHeaders);
		browserApolloClients.set(key, client);
	}

	// Merge SSR-shipped cache state into the existing client cache.
	//
	// Why the `Object.keys(...).length > 0` guard:
	// `_app.page.tsx` calls `useApollo(language, tld, initialApolloState || {})`,
	// so when SSR did not ship any Apollo cache state we still receive a fresh
	// `{}` here. Without this guard, `if (initialState)` would be truthy on
	// every render, causing a needless `cache.extract()` + `cache.restore()`
	// round-trip (cheap when the cache is empty, but linear in cache size
	// once any cached entities exist). The guard makes the merge a true no-op
	// when there is nothing to merge.
	if (initialState && Object.keys(initialState).length > 0) {
		const existingCache = client.extract();
		client.cache.restore({ ...existingCache, ...initialState });
	}

	return client;
}

export function useApollo(
	language: string,
	tld: string,
	initialState: NormalizedCacheObject | null,
): ApolloClient<NormalizedCacheObject> {
	// `initialState` IS intentionally part of the dependency list.
	//
	// This matches the canonical Apollo + Next.js SSR pattern: when a client-
	// side route change brings in new SSR-hydrated Apollo state (different
	// `pageProps[APOLLO_STATE_PROP_NAME]`), the hook must re-run so the new
	// state is merged into the cache by `initializeApollo`. Memoising only on
	// `[language, tld]` would silently drop hydrated state from later
	// navigations and break any future query that relies on the cache
	// (`cache-first` / `cache-and-network`).
	//
	// This does NOT churn the ApolloClient instance: `initializeApollo`
	// returns the singleton stored in `browserApolloClients` for the current
	// (locale, tld) key, so the `<ApolloProvider client={client}>` reference
	// stays stable across renders. Only the InMemoryCache contents get
	// updated when new SSR state arrives, which is exactly the desired
	// behaviour.
	//
	// Note: `pageProps[APOLLO_STATE_PROP_NAME]` is typically a fresh object
	// reference on every render, so this `useMemo` will recompute on every
	// render. That is acceptable because the work inside is a cheap
	// `Map.get` plus (when there is no shipped state) a skipped restore — see
	// the `Object.keys(...).length > 0` guard in `initializeApollo`.
	return useMemo(() => initializeApollo(language, tld, null, null, false, initialState), [language, tld, initialState]);
}
